/**
 * SVG Cache Manager — pre-generate & manage cached diagram SVGs for all
 * has_diagram=true records (fuel_questions + revise_recap_content).
 * Grouped Subject → Grade → Sub-topic. Single + bulk (sub-topic / full collection)
 * refresh, with background-job polling for bulk runs.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, RefreshCw, Image as ImageIcon, CheckCircle2, AlertCircle, XCircle, Zap,
  Download, Upload,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

const STATUS_STYLE = {
  cached:  { label: "Cached",  variant: "outline", className: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  stale:   { label: "Stale",   variant: "outline", className: "text-amber-700 border-amber-300 bg-amber-50" },
  missing: { label: "Missing", variant: "outline", className: "text-slate-500 border-slate-300 bg-slate-50" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.missing;
  return (
    <Badge variant={s.variant} className={`text-[10px] ${s.className}`} data-testid={`svg-status-chip-${status}`}>
      {s.label}
    </Badge>
  );
}

function groupRecords(records) {
  const bySubject = {};
  for (const r of records) {
    const subject = r.subject || "Unknown";
    const grade = r.grade || "Unknown";
    const subTopic = r.sub_topic || "Unknown";
    bySubject[subject] ??= {};
    bySubject[subject][grade] ??= {};
    bySubject[subject][grade][subTopic] ??= [];
    bySubject[subject][grade][subTopic].push(r);
  }
  return bySubject;
}

export default function SvgCacheManager({ token }) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ cached: 0, stale: 0, missing: 0 });
  const [previewId, setPreviewId] = useState(null);
  const [refreshingIds, setRefreshingIds] = useState(new Set());
  const [job, setJob] = useState(null); // { id, total, completed, label }
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/admin/cr/svg-cache/records", token);
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Failed to load SVG cache records"); return; }
      setRecords(json.records || []);
      setSummary(json.summary || { cached: 0, stale: 0, missing: 0 });
    } catch {
      toast.error("Network error loading SVG cache records");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const grouped = useMemo(() => groupRecords(records), [records]);

  const refreshOne = async (recordId) => {
    setRefreshingIds((prev) => new Set(prev).add(recordId));
    try {
      const res = await apiFetch(`/admin/cr/svg-cache/${recordId}/generate`, token, { method: "POST" });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || `Failed to generate ${recordId}`); return; }
      toast.success(`Generated SVG for ${recordId}`);
      await load();
    } catch {
      toast.error("Network error generating SVG");
    } finally {
      setRefreshingIds((prev) => { const n = new Set(prev); n.delete(recordId); return n; });
    }
  };

  const startBatch = async (recordIds, label) => {
    if (!recordIds.length) { toast.info("Nothing to refresh — all records in this scope are already cached."); return; }
    try {
      const res = await apiFetch("/admin/cr/svg-cache/batch-generate", token, {
        method: "POST",
        body: JSON.stringify({ record_ids: recordIds }),
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Failed to start batch"); return; }
      setJob({ id: json.job_id, total: json.total, completed: 0, label });
    } catch {
      toast.error("Network error starting batch generation");
    }
  };

  useEffect(() => {
    if (!job || !job.id) return;
    const poll = async () => {
      try {
        const res = await apiFetch(`/admin/cr/svg-cache/batch-generate/status/${job.id}`, token);
        const json = await res.json();
        if (!res.ok) return;
        setJob((prev) => prev && prev.id === job.id ? { ...prev, completed: json.completed, status: json.status } : prev);
        if (json.status === "done") {
          clearInterval(pollRef.current);
          const failed = (json.results || []).filter((r) => r.status === "error").length;
          toast.success(`${job.label} complete — ${json.total - failed}/${json.total} generated${failed ? `, ${failed} failed` : ""}`);
          await load();
          setTimeout(() => setJob(null), 2500);
        }
      } catch { /* keep polling */ }
    };
    pollRef.current = setInterval(poll, 2000);
    poll();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  const testBatch20 = () => {
    const missing = records.filter((r) => r.status !== "cached").slice(0, 20).map((r) => r.record_id);
    startBatch(missing, "Test batch (20 records)");
  };

  const fullBatch = () => {
    const needsWork = records.filter((r) => r.status !== "cached").map((r) => r.record_id);
    startBatch(needsWork, "Full collection refresh");
  };

  const subTopicBatch = (subTopicRecords, subTopic) => {
    const ids = subTopicRecords.map((r) => r.record_id);
    startBatch(ids, `"${subTopic}" refresh`);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/svg-cache/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `svg_cache_export_${Date.now()}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("SVG cache exported");
    } catch {
      toast.error("Network error exporting SVG cache");
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/admin/cr/svg-cache/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Import failed"); return; }
      toast.success(`Import complete — ${json.inserted} inserted, ${json.updated} updated, ${json.skipped} skipped`);
      await load();
    } catch {
      toast.error("Network error importing SVG cache");
    } finally {
      setImporting(false);
    }
  };

  const preview = records.find((r) => r.record_id === previewId);

  return (
    <div className="p-8 space-y-5" data-testid="svg-cache-manager">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-800">SVG Cache Manager</h2>
          <p className="text-sm text-slate-500">Pre-generated diagram SVGs for has_diagram records — 340 total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="svg-cache-refresh-list-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Reload
          </Button>
          <Button
            size="sm"
            onClick={testBatch20}
            disabled={!!job}
            data-testid="svg-cache-test-batch-btn"
          >
            <Zap className="w-3.5 h-3.5 mr-1.5" /> Generate Test Batch (20)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={fullBatch}
            disabled={!!job}
            data-testid="svg-cache-full-batch-btn"
          >
            Generate All ({summary.stale + summary.missing})
          </Button>
        </div>
      </div>

      {job && (
        <Card className="p-4 border border-sky-200 bg-sky-50/50" data-testid="svg-cache-job-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-sky-800 flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {job.label} — {job.completed} of {job.total}
            </span>
            <span className="text-xs text-sky-600">{job.status === "done" ? "Done" : "Running…"}</span>
          </div>
          <Progress value={job.total ? (job.completed / job.total) * 100 : 0} />
        </Card>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border border-emerald-200 bg-emerald-50/40" data-testid="svg-summary-cached">
          <div className="text-xs text-emerald-700">Cached</div>
          <div className="text-2xl font-bold text-emerald-800">{summary.cached}</div>
        </Card>
        <Card className="p-4 border border-amber-200 bg-amber-50/40" data-testid="svg-summary-stale">
          <div className="text-xs text-amber-700">Stale</div>
          <div className="text-2xl font-bold text-amber-800">{summary.stale}</div>
        </Card>
        <Card className="p-4 border border-slate-200 bg-slate-50/60" data-testid="svg-summary-missing">
          <div className="text-xs text-slate-600">Missing</div>
          <div className="text-2xl font-bold text-slate-700">{summary.missing}</div>
        </Card>
      </div>

      <Card className="p-4 border border-slate-200 bg-slate-50/40" data-testid="svg-cache-sync-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Sync (preview ↔ production)</h3>
            <p className="text-xs text-slate-500">Export the cache as a JSON file, then import it on another environment to avoid regenerating SVGs.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="svg-cache-export-btn">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="svg-cache-import-btn">
              {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleImportFile}
              data-testid="svg-cache-import-file-input"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
      ) : (
        <Card className="p-5 border border-slate-200 shadow-none">
          <Accordion type="multiple" className="space-y-1" data-testid="svg-cache-subjects">
            {Object.entries(grouped).map(([subject, grades]) => {
              const subjectCount = Object.values(grades).reduce((n, st) => n + Object.values(st).reduce((m, recs) => m + recs.length, 0), 0);
              return (
                <AccordionItem key={subject} value={subject} className="border border-slate-200 rounded-lg px-3" data-testid={`svg-subject-${subject}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <span className="text-sm font-medium text-slate-700">{subject}</span>
                    <Badge variant="outline" className="ml-2 mr-auto">{subjectCount} records</Badge>
                  </AccordionTrigger>
                  <AccordionContent>
                    <Accordion type="multiple" className="space-y-1">
                      {Object.entries(grades).map(([grade, subTopics]) => {
                        const gradeCount = Object.values(subTopics).reduce((n, recs) => n + recs.length, 0);
                        return (
                          <AccordionItem key={grade} value={`${subject}-${grade}`} className="border border-slate-100 rounded-lg px-3 bg-slate-50/60" data-testid={`svg-grade-${subject}-${grade}`}>
                            <AccordionTrigger className="hover:no-underline py-2.5">
                              <span className="text-xs font-medium text-slate-600">{grade}</span>
                              <Badge variant="secondary" className="ml-2 mr-auto text-[10px]">{gradeCount}</Badge>
                            </AccordionTrigger>
                            <AccordionContent>
                              <Accordion type="multiple" className="space-y-1">
                                {Object.entries(subTopics).map(([subTopic, recs]) => (
                                  <AccordionItem key={subTopic} value={`${subject}-${grade}-${subTopic}`} className="border border-slate-100 rounded-lg px-3 bg-white" data-testid={`svg-subtopic-${subTopic}`}>
                                    <AccordionTrigger className="hover:no-underline py-2">
                                      <span className="text-xs text-slate-600 text-left">{subTopic}</span>
                                      <div className="flex items-center gap-1.5 ml-2 mr-auto">
                                        <Badge variant="outline" className="text-[10px]">{recs.length}</Badge>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                      <div className="flex justify-end mb-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 text-xs"
                                          disabled={!!job}
                                          onClick={() => subTopicBatch(recs, subTopic)}
                                          data-testid={`svg-subtopic-refresh-${subTopic}`}
                                        >
                                          <RefreshCw className="w-3 h-3 mr-1" /> Refresh this sub-topic
                                        </Button>
                                      </div>
                                      <div className="space-y-1">
                                        {recs.map((r) => (
                                          <div
                                            key={r.record_id}
                                            className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-100 rounded-md px-2.5 py-1.5"
                                            data-testid={`svg-record-row-${r.record_id}`}
                                          >
                                            <button
                                              type="button"
                                              className="text-slate-700 font-mono hover:text-sky-600 truncate text-left"
                                              onClick={() => setPreviewId(r.record_id)}
                                              data-testid={`svg-preview-btn-${r.record_id}`}
                                            >
                                              {r.record_id}
                                            </button>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                              <Badge variant="outline" className="text-[10px]">{r.diagram_type}</Badge>
                                              <StatusChip status={r.status} />
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                disabled={refreshingIds.has(r.record_id)}
                                                onClick={() => refreshOne(r.record_id)}
                                                data-testid={`svg-refresh-btn-${r.record_id}`}
                                              >
                                                {refreshingIds.has(r.record_id)
                                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                                  : <RefreshCw className="w-3 h-3" />}
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          {records.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">No has_diagram records found.</p>
          )}
        </Card>
      )}

      <Dialog open={!!previewId} onOpenChange={(open) => !open && setPreviewId(null)}>
        <DialogContent className="max-w-lg" data-testid="svg-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono text-sm">
              <ImageIcon className="w-4 h-4 text-slate-400" /> {previewId}
            </DialogTitle>
            <DialogDescription>
              {preview?.diagram_type} · {preview?.diagram_description}
            </DialogDescription>
          </DialogHeader>
          <SvgPreviewBody recordId={previewId} token={token} onRefreshed={load} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SvgPreviewBody({ recordId, token, onRefreshed }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!recordId) return;
    setLoading(true);
    apiFetch(`/admin/cr/svg-cache/${recordId}`, token)
      .then((res) => res.json())
      .then(setDetail)
      .catch(() => toast.error("Failed to load preview"))
      .finally(() => setLoading(false));
  }, [recordId, token]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await apiFetch(`/admin/cr/svg-cache/${recordId}/generate`, token, { method: "POST" });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Refresh failed"); return; }
      setDetail((prev) => ({ ...prev, svg: json.svg, status: "cached" }));
      toast.success("SVG regenerated");
      onRefreshed?.();
    } catch {
      toast.error("Network error refreshing SVG");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!detail) return <p className="text-sm text-slate-400 py-4">Could not load record.</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <StatusChip status={detail.status} />
        {detail.status === "missing" && <span className="text-xs text-slate-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Not generated yet</span>}
        {detail.status === "stale" && <span className="text-xs text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Source changed since last cache</span>}
        {detail.status === "cached" && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Up to date</span>}
      </div>
      <div
        className="border border-slate-200 rounded-lg bg-white flex items-center justify-center p-3 min-h-[220px]"
        data-testid="svg-preview-canvas"
      >
        {detail.svg ? (
          <div
            className="w-full [&_svg]:w-full [&_svg]:max-w-[380px] [&_svg]:h-auto [&_svg]:mx-auto"
            dangerouslySetInnerHTML={{ __html: detail.svg }}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-300">
            <XCircle className="w-8 h-8" />
            <span className="text-xs">No SVG cached yet</span>
          </div>
        )}
      </div>
      <Button size="sm" className="w-full" onClick={refresh} disabled={refreshing} data-testid="svg-preview-refresh-btn">
        {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
        {detail.status === "missing" ? "Generate" : "Refresh"}
      </Button>
    </div>
  );
}
